import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { useApi } from '../../../../hooks/useApi';
import { UnifiedTable } from '../../../../components/UnifiedTable';
import {
  loadProfitabilityProducts,
  saveProfitabilityCogs,
  loadProfitabilityDrr,
  bulkChangePrice,
} from '../../../../services/ozonPrices';
import './OzonProfitabilityPanel.css';
import { filterBySearch } from '../../../../utils/search';

// ── Helpers ──────────────────────────────────────────────────────────────────

function pf(v, fallback = 0) {
  const n = parseFloat(v);
  return isNaN(n) ? fallback : n;
}

function fmt(v, digits = 1) {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return v.toFixed(digits).replace('.', ',');
}

function fmtRub(v) {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return Math.round(v).toLocaleString('ru-RU') + ' ₽';
}

function trafficLight(pct) {
  if (pct === null || isNaN(pct)) return '';
  if (pct < 0) return 'red';
  if (pct < 10) return 'yellow';
  return 'green';
}

// ── FBS Calculations ──────────────────────────────────────────────────────────

const FBS_SCENARIOS = {
  courier: [
    { key: 'slot1',  label: 'Слот 1 (≤12ч) — скидка −3%',       adj: -3, minPenalty: 0   },
    { key: 'slot2',  label: 'Слот 2 (12–24ч) — скидка −2%',      adj: -2, minPenalty: 0   },
    { key: 'normal', label: 'Стандарт — без изменений',           adj:  0, minPenalty: 0   },
    { key: 'late1',  label: 'Просрочка 12ч — +1% (мин 50₽)',      adj: +1, minPenalty: 50  },
    { key: 'late2',  label: 'Просрочка 24ч — +2% (мин 100₽)',     adj: +2, minPenalty: 100 },
    { key: 'late3',  label: 'Просрочка 48ч+ — +3% (мин 100₽)',    adj: +3, minPenalty: 100 },
  ],
  pvz: [
    { key: 'slot1',  label: 'Рек. слот — скидка −2%',             adj: -2, minPenalty: 0   },
    { key: 'normal', label: 'Стандарт — без изменений',            adj:  0, minPenalty: 0   },
    { key: 'late1',  label: 'Просрочка 24ч — +1% (мин 50₽)',       adj: +1, minPenalty: 50  },
    { key: 'late2',  label: 'Просрочка 48ч — +2% (мин 100₽)',      adj: +2, minPenalty: 100 },
    { key: 'late3',  label: 'Просрочка 48ч+ — +3% (мин 100₽)',     adj: +3, minPenalty: 100 },
  ],
};

function calcFBS(row, fbsScenarioDef) {
  const { adj = 0, minPenalty = 0 } = fbsScenarioDef || {};
  const price = pf(row.price);
  const cogs = pf(row.cogs, NaN);
  const drr = pf(row.drr_override, 0);

  const commPct = pf(row.fbs_commission) / 100;
  const acquiring = row.acquiring != null ? pf(row.acquiring) : price * 0.01;
  const baseLog = pf(row.fbs_first_mile_max) + pf(row.fbs_direct_flow_trans_max);
  const adCost = price * drr / 100;

  // adj — корректировка ЛОГИСТИКИ (не комиссии): штраф за просрочку или скидка за ранний слот
  let logAdj = 0;
  if (adj !== 0 && price > 0) {
    logAdj = adj > 0
      ? Math.max(price * adj / 100, minPenalty)
      : price * adj / 100;
  }
  const fbsLog = baseLog + logAdj;
  const commCost = price * commPct;

  const cogsSafe = isNaN(cogs) ? 0 : cogs;
  const profit = price - commCost - fbsLog - acquiring - adCost - cogsSafe;
  const hasCogs = !isNaN(cogs) && cogs > 0;

  return {
    log: fbsLog,
    baseLog,
    logAdj,
    commPct: commPct * 100,
    acquiring, adCost,
    profit: price > 0 ? profit : NaN,
    margin: price > 0 ? (profit / price) * 100 : NaN,
    profitability: hasCogs ? (profit / cogs) * 100 : NaN,
  };
}

// targetRetPct: целевая рентабельность (profit / cogs * 100)
// requires cogs > 0, otherwise returns null
// Доля эквайринга — та же, что в текущей прибыли: из API Ozon, если он её отдал,
// иначе 1%. С фиксированным 1% цель не давала заявленную рентабельность.
function acquiringShare(row) {
  const price = pf(row.price);
  if (row.acquiring != null && price > 0) return pf(row.acquiring) / price;
  return 0.01;
}

function targetFBS(row, targetRetPct, fbsScenarioDef) {
  const { adj = 0, minPenalty = 0 } = fbsScenarioDef || {};
  const cogs = pf(row.cogs, 0);
  if (cogs <= 0) return null;
  const acq = acquiringShare(row);
  const drr = pf(row.drr_override, 0);
  const tr = targetRetPct / 100;
  const commPct = pf(row.fbs_commission) / 100;
  const baseLog = pf(row.fbs_first_mile_max) + pf(row.fbs_direct_flow_trans_max);
  // adj — логистическая корректировка (% от цены); сначала решаем без floor
  // price*(1 - commPct - 0.01 - adj/100 - drr/100) = baseLog + cogs*(1 + tr)
  const adjFrac = adj / 100;
  const denom = 1 - commPct - acq - adjFrac - drr / 100;
  if (denom <= 0) return null;
  let raw = Math.ceil((baseLog + cogs * (1 + tr)) / denom);
  // Если minPenalty больше %-й части — переcчитываем с фиксированным штрафом
  if (adj > 0 && minPenalty > 0 && raw * adj / 100 < minPenalty) {
    const d2 = 1 - commPct - acq - drr / 100;
    if (d2 <= 0) return null;
    raw = Math.ceil((baseLog + minPenalty + cogs * (1 + tr)) / d2);
  }
  return raw;
}

// ── FBO Calculations ──────────────────────────────────────────────────────────

// localPct: % локальных продаж (из кабинета Ozon, 0–100)
// surcharge: наценка за нелокальные, % от цены (0–12, из таблицы кластеров)
// lastMile: доставка до места выдачи, ₽ (фиксированная небольшая сумма)
function calcFBO(row, { localPct, surcharge, lastMile }) {
  const price = pf(row.price);
  const cogs = pf(row.cogs, NaN);
  const drr = pf(row.drr_override, 0);

  const commPct = pf(row.fbo_commission) / 100;
  const acquiring = row.acquiring != null ? pf(row.acquiring) : price * 0.01;
  // min — базовый тариф без нелокальной наценки (max уже включает её в worst case)
  const baseLog = pf(row.fbo_direct_flow_trans_min);
  const nonLocalFrac = (100 - pf(localPct, 0)) / 100;
  const surchargeAmt = price * (pf(surcharge, 0) / 100) * nonLocalFrac;
  const fboLog = baseLog + surchargeAmt + pf(lastMile, 0);
  const adCost = price * drr / 100;

  const cogsSafe = isNaN(cogs) ? 0 : cogs;
  const profit = price - price * commPct - acquiring - fboLog - adCost - cogsSafe;
  const hasCogs = !isNaN(cogs) && cogs > 0;

  return {
    log: fboLog,
    baseLog, surchargeAmt,
    commPct: commPct * 100,
    acquiring, adCost,
    profit: price > 0 ? profit : NaN,
    margin: price > 0 ? (profit / price) * 100 : NaN,
    profitability: hasCogs ? (profit / cogs) * 100 : NaN,
  };
}

function targetFBO(row, targetRetPct, { localPct, surcharge, lastMile }) {
  const cogs = pf(row.cogs, 0);
  if (cogs <= 0) return null;
  const acq = acquiringShare(row);
  const drr = pf(row.drr_override, 0);
  const tr = targetRetPct / 100;
  const commPct = pf(row.fbo_commission) / 100;
  const nonLocalFrac = (100 - pf(localPct, 0)) / 100;
  const surchargeFrac = (pf(surcharge, 0) / 100) * nonLocalFrac;
  const baseLog = pf(row.fbo_direct_flow_trans_min);
  const lastMileVal = pf(lastMile, 0);
  // price*(1 - commPct - 0.01 - surchargeFrac - drr/100) = baseLog + lastMile + cogs*(1 + tr)
  const denom = 1 - commPct - acq - surchargeFrac - drr / 100;
  if (denom <= 0) return null;
  return Math.ceil((baseLog + lastMileVal + cogs * (1 + tr)) / denom);
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function OzonProfitabilityPanel({ accountId, addLog = () => {}, parseOzonPriceResult = (r, p) => ({ ok: p.length, fail: 0, details: p.map(x => ({ ...x, ok: true, errors: [] })) }) }) {
  const { request, showStatus } = useApi();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedIds, setSelectedIds] = useState([]);
  const [cogsEdits, setCogsEdits] = useState({});
  const [priceEdits, setPriceEdits] = useState({});
  const [targetEdits, setTargetEdits] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loadingDrr, setLoadingDrr] = useState(false);

  const [search, setSearch] = useState('');

  // Mode: 'fbs' | 'fbo'
  const [mode, setMode] = useState('fbs');

  // FBS settings
  const [fbsType, setFbsType] = useState('courier');
  const [fbsScenarioKey, setFbsScenarioKey] = useState('normal');

  // FBO settings — общие для кабинета, хранятся на сервере.
  const [fboLocalPct, setFboLocalPctRaw] = useState(50);
  const [fboSurcharge, setFboSurchargeRaw] = useState(8);
  const [fboLastMile, setFboLastMileRaw] = useState(10);
  const fboSaveTimer = useRef(null);
  const fboLoadedFor = useRef(null);

  const putFboSettings = useCallback(async (next) => {
    try {
      await request(`/api/v1/ozon-prices/${accountId}/profitability/settings`, {
        method: 'PUT',
        body: JSON.stringify({
          fbo_local_pct: next.localPct, fbo_surcharge_pct: next.surcharge, fbo_last_mile_rub: next.lastMile,
        }),
      });
    } catch (e) {
      showStatus('error', 'Настройки FBO не сохранились: ' + (e?.message || ''));
    }
  }, [accountId, request, showStatus]);

  useEffect(() => {
    if (!accountId) return undefined;
    let cancelled = false;
    fboLoadedFor.current = null;
    request(`/api/v1/ozon-prices/${accountId}/profitability/settings`)
      .then(data => {
        if (cancelled) return;
        let next = { localPct: data.fbo_local_pct, surcharge: data.fbo_surcharge_pct, lastMile: data.fbo_last_mile_rub };
        // Раньше настройки жили в браузере — переносим один раз, если на сервере их ещё нет.
        const legacyKey = `ozprof_fbo_${accountId}`;
        let legacy = null;
        try { legacy = JSON.parse(localStorage.getItem(legacyKey)); } catch { legacy = null; }
        if (!data.saved && legacy) {
          next = {
            localPct: legacy.localPct ?? next.localPct,
            surcharge: legacy.surcharge ?? next.surcharge,
            lastMile: legacy.lastMile ?? next.lastMile,
          };
          putFboSettings(next);
        }
        try { localStorage.removeItem(legacyKey); } catch {}
        setFboLocalPctRaw(next.localPct);
        setFboSurchargeRaw(next.surcharge);
        setFboLastMileRaw(next.lastMile);
        fboLoadedFor.current = accountId;
      })
      .catch(e => {
        if (!cancelled) showStatus('error', 'Не удалось загрузить настройки FBO: ' + (e?.message || ''));
      });
    return () => { cancelled = true; clearTimeout(fboSaveTimer.current); };
  }, [accountId, request, showStatus, putFboSettings]);

  function saveFboSettings(patch) {
    // До загрузки с сервера не пишем — иначе значения по умолчанию затрут сохранённые.
    if (fboLoadedFor.current !== accountId) return;
    const next = { localPct: fboLocalPct, surcharge: fboSurcharge, lastMile: fboLastMile, ...patch };
    clearTimeout(fboSaveTimer.current);
    fboSaveTimer.current = setTimeout(() => putFboSettings(next), 600);
  }
  function setFboLocalPct(v) { setFboLocalPctRaw(v); saveFboSettings({ localPct: v }); }
  function setFboSurcharge(v) { setFboSurchargeRaw(v); saveFboSettings({ surcharge: v }); }
  function setFboLastMile(v) { setFboLastMileRaw(v); saveFboSettings({ lastMile: v }); }

  // Shared
  const [targetMarginPct, setTargetMarginPct] = useState(15);
  const [showTarget, setShowTarget] = useState(true);

  const fileRef = useRef(null);

  // ── Load ──────────────────────────────────────────────────────────────────

  const handleLoad = useCallback(async () => {
    setLoading(true);
    try {
      const items = await loadProfitabilityProducts({ request, accountId });
      setProducts(items);
      const initial = {};
      items.forEach(r => {
        if (r.cogs !== null || r.drr_override !== null) {
          initial[r.offer_id] = { cogs: r.cogs ?? '', drr_override: r.drr_override ?? '' };
        }
      });
      setCogsEdits(initial);
      setSelectedIds(items.map(r => r.offer_id));
      setDirty(false);
    } catch (e) {
      showStatus('error', 'Ошибка загрузки: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [accountId, request, showStatus]);

  useEffect(() => { handleLoad(); }, [handleLoad]);

  // ── Load DRR from Performance API ────────────────────────────────────────

  async function handleLoadDrr() {
    setLoadingDrr(true);
    try {
      const drrMap = await loadProfitabilityDrr({ request, accountId });
      const count = Object.keys(drrMap).length;
      if (count === 0) {
        showStatus('warning', 'ДРР: нет данных от Performance API');
        return;
      }
      setCogsEdits(prev => {
        const next = { ...prev };
        Object.entries(drrMap).forEach(([offer_id, drr]) => {
          next[offer_id] = { ...(next[offer_id] || {}), drr_override: String(drr) };
        });
        return next;
      });
      setDirty(true);
      showStatus('success', `ДРР загружен для ${count} товаров`);
    } catch (e) {
      showStatus('error', 'Ошибка загрузки ДРР: ' + e.message);
    } finally {
      setLoadingDrr(false);
    }
  }

  // ── Inline edit ───────────────────────────────────────────────────────────

  function handleCellEdit(offerId, field, value) {
    setCogsEdits(prev => ({
      ...prev,
      [offerId]: { ...(prev[offerId] || {}), [field]: value },
    }));
    setDirty(true);
  }

  // ── XLSX template download / upload ──────────────────────────────────────

  function handleDownloadCogsTemplate() {
    const rows = products.map(p => ({
      'offer_id': p.offer_id,
      'Название': p.name || '',
      'Себестоимость (₽)': cogsEdits[p.offer_id]?.cogs ?? (p.cogs != null ? p.cogs : ''),
      'ДРР (%)': cogsEdits[p.offer_id]?.drr_override ?? (p.drr_override != null ? p.drr_override : ''),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{ wch: 22 }, { wch: 42 }, { wch: 22 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Себестоимости');
    XLSX.writeFile(wb, 'ozon_cogs.xlsx', { bookType: 'xlsx', cellDates: false });
  }

  function handleXlsxImport(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
        const updates = {};
        rows.forEach(row => {
          const oid = String(row['offer_id'] || '').trim();
          if (!oid) return;
          const cogs = String(row['Себестоимость (₽)'] ?? '').replace(',', '.').trim();
          const drr  = String(row['ДРР (%)'] ?? '').replace(',', '.').trim();
          updates[oid] = {};
          if (cogs !== '') updates[oid].cogs = cogs;
          if (drr  !== '') updates[oid].drr_override = drr;
        });
        setCogsEdits(prev => {
          const merged = { ...prev };
          Object.entries(updates).forEach(([oid, vals]) => {
            merged[oid] = { ...(merged[oid] || {}), ...vals };
          });
          return merged;
        });
        setDirty(true);
        showStatus('success', `Импортировано ${Object.keys(updates).length} строк из XLSX`);
      } catch (err) {
        showStatus('error', 'Ошибка чтения файла: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  }

  // ── Save ──────────────────────────────────────────────────────────────────

  async function handleSave() {
    setSaving(true);
    try {
      const items = Object.entries(cogsEdits).map(([offer_id, vals]) => ({
        offer_id,
        cogs: vals.cogs !== '' && vals.cogs != null ? parseFloat(String(vals.cogs).replace(',', '.')) : null,
        drr_override: vals.drr_override !== '' && vals.drr_override != null ? parseFloat(String(vals.drr_override).replace(',', '.')) : null,
      }));
      await saveProfitabilityCogs({ request, accountId, items });
      setDirty(false);
      showStatus('success', 'Сохранено');
    } catch (e) {
      showStatus('error', 'Ошибка сохранения: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Derived data ──────────────────────────────────────────────────────────

  const currentFbsScenario = (FBS_SCENARIOS[fbsType] || FBS_SCENARIOS.courier)
    .find(s => s.key === fbsScenarioKey) || FBS_SCENARIOS.courier[2];

  const fboSettings = { localPct: fboLocalPct, surcharge: fboSurcharge, lastMile: fboLastMile };

  const rows = products.map(row => {
    const edits = cogsEdits[row.offer_id] || {};
    const merged = {
      ...row,
      _origPrice: row.price,
      price: priceEdits[row.offer_id] !== undefined ? priceEdits[row.offer_id] : row.price,
      cogs: edits.cogs !== undefined ? edits.cogs : row.cogs,
      drr_override: edits.drr_override !== undefined ? edits.drr_override : row.drr_override,
    };
    const c = mode === 'fbo'
      ? calcFBO(merged, fboSettings)
      : calcFBS(merged, currentFbsScenario);
    return {
      ...merged,
      _calc: c,
      _stockFbo: pf(row.stock_fbo, 0),
      _stockFbs: pf(row.stock_fbs, 0),
      _stockTotal: pf(row.stock_fbo, 0) + pf(row.stock_fbs, 0),
      _priceNum: pf(merged.price, null),
      _cogsNum: pf(merged.cogs, null),
      _drrNum: pf(merged.drr_override, 0),
      _commPct: c.commPct,
      _log: c.log,
      _acquiring: c.acquiring,
      _profit: c.profit,
      _margin: c.margin,
      _profitability: c.profitability,
    };
  });

  const filtered = filterBySearch(rows, search);

  // ── Export target prices ──────────────────────────────────────────────────

  async function handleExportTargetPrices() {
    const prices = [];
    for (const row of filtered) {
      if (!selectedIds.includes(row.offer_id)) continue;
      const manual = targetEdits[row.offer_id];
      let finalPrice;
      if (manual !== undefined && manual !== '') {
        finalPrice = parseFloat(String(manual).replace(',', '.'));
      } else {
        finalPrice = mode === 'fbo'
          ? targetFBO(row, targetMarginPct, fboSettings)
          : targetFBS(row, targetMarginPct, currentFbsScenario);
      }
      if (finalPrice && !isNaN(finalPrice) && finalPrice > 0) {
        prices.push({ offer_id: row.offer_id, price: String(Math.round(finalPrice)) });
      }
    }
    if (!prices.length) { showStatus('warning', 'Нет цен для выгрузки'); return; }
    const preview = prices.slice(0, 10).map(p => `${p.offer_id}: ${p.price} ₽`).join('\n');
    if (!window.confirm(
      `Изменить цену в Ozon у ${prices.length} товаров?\n\n${preview}${prices.length > 10 ? `\n…и ещё ${prices.length - 10}` : ''}`
      + '\n\nЦены ниже нижней рентабельной (защита) не отправятся.'
    )) return;
    setExporting(true);
    try {
      const result = await bulkChangePrice({ request, accountId, prices });
      const { ok, fail, details } = parseOzonPriceResult(result, prices);
      addLog({ op: 'Выгрузка цен (Unit)', source: 'profitability', total: prices.length, ok, fail, details });
      showStatus(fail > 0 ? 'warning' : 'success',
        fail > 0 ? `Обновлено: ${ok}, ошибок: ${fail}` : `Обновлено ${ok} цен`);
    } catch (e) {
      showStatus('error', 'Ошибка выгрузки: ' + e.message);
    } finally {
      setExporting(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const columns = useMemo(() => {
    const cols = [
      {
        key: 'name',
        header: 'Товар',
        width: 280,
        sticky: true,
        type: 'custom',
        render: (value, row) => (
          <div className="ozprof__td--name">
            {row.image_url && <img src={row.image_url} alt="" className="ozprof__img" />}
            <div>
              <div className="ozprof__offer-id">{row.offer_id}</div>
              <div className="ozprof__name">{row.name}</div>
            </div>
          </div>
        ),
      },
      {
        key: '_stockTotal',
        header: 'Остаток FBO / FBS',
        width: 140,
        type: 'number',
        render: (value, row) => `${row._stockFbo} / ${row._stockFbs}`,
      },
      {
        key: '_priceNum',
        header: 'Цена ₽ (расчёт)',
        title: 'Цена для расчёта «что если» — в Ozon не отправляется. Отправка — кнопкой «Выгрузить цели в Ozon».',
        width: 110,
        type: 'number',
        render: (value, row) => (
          <input
            className="ozprof__cell-input"
            type="text"
            value={priceEdits[row.offer_id] !== undefined ? priceEdits[row.offer_id] : (row._origPrice != null ? String(row._origPrice) : '')}
            placeholder="—"
            onClick={e => e.stopPropagation()}
            onChange={e => setPriceEdits(prev => ({ ...prev, [row.offer_id]: e.target.value }))}
          />
        ),
      },
      {
        key: '_cogsNum',
        header: 'Себест. ₽',
        width: 100,
        type: 'number',
        render: (value, row) => {
          const cogsVal = cogsEdits[row.offer_id]?.cogs ?? row.cogs ?? '';
          return (
            <input
              className="ozprof__cell-input"
              type="text"
              value={cogsVal}
              placeholder="0"
              onClick={e => e.stopPropagation()}
              onChange={e => handleCellEdit(row.offer_id, 'cogs', e.target.value)}
            />
          );
        },
      },
      {
        key: '_drrNum',
        header: 'ДРР общий, %',
        width: 110,
        type: 'number',
        render: (value, row) => {
          const drrVal = cogsEdits[row.offer_id]?.drr_override ?? row.drr_override ?? '';
          return (
            <input
              className="ozprof__cell-input"
              type="text"
              value={drrVal}
              placeholder="0"
              onClick={e => e.stopPropagation()}
              onChange={e => handleCellEdit(row.offer_id, 'drr_override', e.target.value)}
            />
          );
        },
      },
      {
        key: '_commPct',
        header: mode === 'fbo' ? 'Ком. FBO %' : 'Ком. FBS %',
        width: 100,
        type: 'number',
        render: value => `${fmt(value)}%`,
      },
      {
        key: '_log',
        header: 'Лог. ₽',
        width: 120,
        type: 'number',
        render: (value, row) => {
          const c = row._calc;
          const title = mode === 'fbo'
            ? `База: ${fmtRub(c.baseLog)}, наценка: ${fmtRub(c.surchargeAmt)}`
            : (c.logAdj ? `База: ${fmtRub(c.baseLog)}, корр.: ${fmtRub(c.logAdj)}` : undefined);
          return (
            <span title={title}>
              {isNaN(c.log) ? '—' : fmtRub(c.log)}
              {mode === 'fbo' && c.surchargeAmt > 0 && (
                <span className="ozprof__adj red"> +{fmtRub(c.surchargeAmt)}</span>
              )}
              {mode === 'fbs' && c.logAdj !== 0 && (
                <span className={`ozprof__adj ${c.logAdj < 0 ? 'green' : 'red'}`}>
                  {c.logAdj > 0 ? ` +${fmtRub(c.logAdj)}` : ` −${fmtRub(Math.abs(c.logAdj))}`}
                </span>
              )}
            </span>
          );
        },
      },
      {
        key: '_acquiring',
        header: 'Эквайр. ₽',
        width: 100,
        type: 'number',
        render: value => isNaN(value) ? '—' : fmtRub(value),
      },
      {
        key: '_profit',
        header: 'Прибыль ₽',
        width: 110,
        type: 'number',
        render: value => <span className={`ozprof__profit ${trafficLight(value)}`}>{isNaN(value) ? '—' : fmtRub(value)}</span>,
      },
      {
        key: '_margin',
        header: 'Маржа %',
        width: 100,
        type: 'number',
        render: value => <span className={`ozprof__metric ${trafficLight(value)}`}>{isNaN(value) ? '—' : fmt(value) + '%'}</span>,
      },
      {
        key: '_profitability',
        header: 'Рент. %',
        width: 100,
        type: 'number',
        render: value => <span className={`ozprof__metric ${trafficLight(value)}`}>{isNaN(value) ? '—' : fmt(value) + '%'}</span>,
      },
    ];

    if (showTarget) {
      cols.push({
        key: '_target',
        header: 'Цель ₽',
        width: 110,
        sortable: false,
        type: 'custom',
        render: (value, row) => {
          const tPrice = mode === 'fbo'
            ? targetFBO(row, targetMarginPct, fboSettings)
            : targetFBS(row, targetMarginPct, currentFbsScenario);
          return (
            <input
              className="ozprof__cell-input ozprof__cell-input--target"
              type="text"
              value={targetEdits[row.offer_id] !== undefined ? targetEdits[row.offer_id] : (tPrice ? String(tPrice) : '')}
              placeholder={tPrice ? String(tPrice) : '—'}
              onClick={e => e.stopPropagation()}
              onChange={e => setTargetEdits(prev => ({ ...prev, [row.offer_id]: e.target.value }))}
            />
          );
        },
      });
    }

    return cols;
  }, [mode, priceEdits, cogsEdits, targetEdits, showTarget, targetMarginPct, currentFbsScenario, fboSettings]);

  return (
    <div className="ozprof">
      {/* Toolbar */}
      <div className="ozprof__toolbar">
        <button className="btn btn--primary" onClick={handleLoad} disabled={loading}>
          {loading ? 'Загрузка…' : 'Загрузить данные'}
        </button>
        {products.length > 0 && (
          <>
            <button className="btn btn--secondary" onClick={handleLoadDrr} disabled={loadingDrr}>
              {loadingDrr ? 'Загрузка ДРР…' : 'Загрузить ДРР (30д)'}
            </button>
            <button className="btn btn--secondary" onClick={handleDownloadCogsTemplate}>
              Скачать шаблон XLSX
            </button>
            <button className="btn btn--secondary" onClick={() => fileRef.current?.click()}>
              Загрузить XLSX
            </button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleXlsxImport} />
            {dirty && (
              <button className="btn btn--primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Сохранение…' : 'Сохранить себестоимости'}
              </button>
            )}
            <button className="btn btn--accent" onClick={handleExportTargetPrices} disabled={exporting}>
              {exporting ? 'Выгрузка…' : 'Выгрузить цели в Ozon'}
            </button>
          </>
        )}
      </div>

      {/* Settings */}
      {products.length > 0 && (
        <div className="ozprof__settings">
          {/* Mode toggle */}
          <label>
            Режим
            <div className="ozprof__toggle-group">
              <button
                type="button"
                className={`ozprof__toggle-btn${mode === 'fbs' ? ' active' : ''}`}
                onClick={() => setMode('fbs')}
              >FBS</button>
              <button
                type="button"
                className={`ozprof__toggle-btn${mode === 'fbo' ? ' active' : ''}`}
                onClick={() => setMode('fbo')}
              >FBO</button>
            </div>
          </label>

          {mode === 'fbs' && (
            <>
              <label>
                Способ FBS
                <div className="ozprof__toggle-group">
                  <button
                    type="button"
                    title="Передача курьеру Ozon или сдача в сортировочный центр — жёсткие слоты, скидка до −3% за ранний"
                    className={`ozprof__toggle-btn${fbsType === 'courier' ? ' active' : ''}`}
                    onClick={() => { setFbsType('courier'); setFbsScenarioKey('normal'); }}
                  >Курьер / СЦ</button>
                  <button
                    type="button"
                    title="Сдача в пункт выдачи заказов или партнёрский ПВЗ — один рекомендуемый слот, скидка −2%"
                    className={`ozprof__toggle-btn${fbsType === 'pvz' ? ' active' : ''}`}
                    onClick={() => { setFbsType('pvz'); setFbsScenarioKey('normal'); }}
                  >ПВЗ / ППЗ</button>
                </div>
              </label>
              <label>
                Сценарий
                <select
                  value={fbsScenarioKey}
                  onChange={e => setFbsScenarioKey(e.target.value)}
                  className="ozprof__select"
                >
                  {FBS_SCENARIOS[fbsType].map(s => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </label>
            </>
          )}

          {mode === 'fbo' && (
            <>
              <label>
                Доля локальных продаж, %
                <input
                  type="number" step="1" min="0" max="100"
                  value={fboLocalPct}
                  onChange={e => setFboLocalPct(parseFloat(e.target.value) || 0)}
                />
              </label>
              <label>
                Наценка за нелокальные, %
                <input
                  type="number" step="1" min="0" max="12"
                  value={fboSurcharge}
                  onChange={e => setFboSurcharge(parseFloat(e.target.value) || 0)}
                />
              </label>
              <label>
                Доставка до выдачи, ₽
                <input
                  type="number" step="1" min="0"
                  value={fboLastMile}
                  onChange={e => setFboLastMile(parseFloat(e.target.value) || 0)}
                />
              </label>
              <span className="ozprof__settings-note">
                Нелокальные: {100 - fboLocalPct}% × {fboSurcharge}% = эфф. наценка {fmt((100 - fboLocalPct) * fboSurcharge / 100)}%
              </span>
            </>
          )}

          <label className="ozprof__settings-toggle">
            <input type="checkbox" checked={showTarget} onChange={e => setShowTarget(e.target.checked)} />
            Целевая цена
          </label>
          {showTarget && (
            <label>
              Целевая рентабельность, %
              <input
                type="number" step="1" min="-100" max="500"
                value={targetMarginPct}
                onChange={e => setTargetMarginPct(parseFloat(e.target.value) || 0)}
              />
            </label>
          )}

          <span className="ozprof__settings-note">Эквайринг: из API Ozon</span>
        </div>
      )}

      {products.length > 0 && (
        <div className="ozprof__hint">
          XLSX-шаблон: колонки offer_id, Название, Себестоимость (₽), ДРР (%). Скачайте шаблон, заполните и загрузите обратно.
        </div>
      )}

      {/* Table */}
      {products.length > 0 && (
        <UnifiedTable
          tableId="ozon-profitability"
          columns={columns}
          data={filtered}
          getRowKey={row => row.offer_id}
          search={search}
          onSearchChange={setSearch}
          selectable
          selectedRows={selectedIds}
          onSelectionChange={setSelectedIds}
          loading={loading}
          exportFilename="ozon_profitability"
        />
      )}

      {products.length === 0 && !loading && (
        <div className="ozprof__empty">
          Нажмите «Загрузить данные» — подтянем ассортимент, цены и тарифы логистики из Ozon.
        </div>
      )}
    </div>
  );
}
